import { doesConcretePathMatchPattern, splitPath } from './endpoint-identity';

export type ConcretePathMatchIndex = {
  readonly matchingPaths: (pattern: string) => readonly string[];
};

export type PatternMatchIndex = {
  readonly matchingPatterns: (concretePath: string) => readonly string[];
  readonly hasMatchingPattern: (concretePath: string) => boolean;
};

type OrderedPattern = {
  readonly pattern: string;
  readonly order: number;
};

const NO_PATHS: readonly string[] = [];
const NO_PATTERNS: readonly OrderedPattern[] = [];

const concretePathMatchIndexes = new WeakMap<
  readonly string[],
  { readonly indexedLength: number; readonly index: ConcretePathMatchIndex }
>();

function isExactLiteralSegment(segment: string): boolean {
  return !segment.includes('{') && !segment.startsWith(':');
}

function segmentKey({
  depth,
  position,
  segment,
}: {
  depth: number;
  position: number;
  segment: string;
}): string {
  return `${depth}:${position}:${segment}`;
}

function literalSegmentKeys(segments: readonly string[]): string[] {
  return segments.flatMap((segment, position) =>
    isExactLiteralSegment(segment)
      ? [segmentKey({ depth: segments.length, position, segment })]
      : [],
  );
}

function appendToBucket<K, V>(buckets: Map<K, V[]>, key: K, value: V): void {
  const bucket = buckets.get(key);

  if (bucket) {
    bucket.push(value);
  } else {
    buckets.set(key, [value]);
  }
}

function smallestBucket<T>(
  buckets: readonly (readonly T[])[],
): readonly T[] | undefined {
  return buckets.reduce<readonly T[] | undefined>(
    (smallest, bucket) =>
      smallest === undefined || bucket.length < smallest.length
        ? bucket
        : smallest,
    undefined,
  );
}

export function createConcretePathMatchIndex(
  concretePaths: readonly string[],
): ConcretePathMatchIndex {
  const byDepth = new Map<number, string[]>();
  const bySegmentKey = new Map<string, string[]>();

  for (const concretePath of concretePaths) {
    const segments = splitPath(concretePath);

    appendToBucket(byDepth, segments.length, concretePath);
    segments.forEach((segment, position) =>
      appendToBucket(
        bySegmentKey,
        segmentKey({ depth: segments.length, position, segment }),
        concretePath,
      ),
    );
  }

  const matchesByPattern = new Map<string, readonly string[]>();

  return {
    matchingPaths: (pattern) => {
      const memoized = matchesByPattern.get(pattern);

      if (memoized) {
        return memoized;
      }

      const patternSegments = splitPath(pattern);
      const candidates =
        smallestBucket(
          literalSegmentKeys(patternSegments).map(
            (key) => bySegmentKey.get(key) ?? NO_PATHS,
          ),
        ) ??
        byDepth.get(patternSegments.length) ??
        NO_PATHS;
      const matches = candidates.filter((concretePath) =>
        doesConcretePathMatchPattern(concretePath, pattern),
      );

      matchesByPattern.set(pattern, matches);

      return matches;
    },
  };
}

export function concretePathMatchIndexFor(
  concretePaths: readonly string[],
): ConcretePathMatchIndex {
  const cached = concretePathMatchIndexes.get(concretePaths);

  if (cached && cached.indexedLength === concretePaths.length) {
    return cached.index;
  }

  const index = createConcretePathMatchIndex(concretePaths);

  concretePathMatchIndexes.set(concretePaths, {
    indexedLength: concretePaths.length,
    index,
  });

  return index;
}

export function createPatternMatchIndex(
  patterns: readonly string[],
): PatternMatchIndex {
  const literalKeysByPattern = patterns.map((pattern) =>
    literalSegmentKeys(splitPath(pattern)),
  );
  const patternCountByKey = literalKeysByPattern
    .flat()
    .reduce(
      (counts, key) => counts.set(key, (counts.get(key) ?? 0) + 1),
      new Map<string, number>(),
    );
  const bySegmentKey = new Map<string, OrderedPattern[]>();
  const byDepthWithoutLiteralSegment = new Map<number, OrderedPattern[]>();

  patterns.forEach((pattern, order) => {
    const [rarestKey] = [...literalKeysByPattern[order]].sort(
      (left, right) =>
        (patternCountByKey.get(left) ?? 0) -
        (patternCountByKey.get(right) ?? 0),
    );

    if (rarestKey === undefined) {
      appendToBucket(byDepthWithoutLiteralSegment, splitPath(pattern).length, {
        pattern,
        order,
      });
    } else {
      appendToBucket(bySegmentKey, rarestKey, { pattern, order });
    }
  });

  const candidateBuckets = (
    concretePath: string,
  ): readonly (readonly OrderedPattern[])[] => {
    const segments = splitPath(concretePath);

    return [
      ...segments.map(
        (segment, position) =>
          bySegmentKey.get(
            segmentKey({ depth: segments.length, position, segment }),
          ) ?? NO_PATTERNS,
      ),
      byDepthWithoutLiteralSegment.get(segments.length) ?? NO_PATTERNS,
    ];
  };

  return {
    matchingPatterns: (concretePath) =>
      candidateBuckets(concretePath)
        .flat()
        .filter(({ pattern }) =>
          doesConcretePathMatchPattern(concretePath, pattern),
        )
        .sort((left, right) => left.order - right.order)
        .map(({ pattern }) => pattern),
    hasMatchingPattern: (concretePath) =>
      candidateBuckets(concretePath).some((bucket) =>
        bucket.some(({ pattern }) =>
          doesConcretePathMatchPattern(concretePath, pattern),
        ),
      ),
  };
}
